import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, GripVertical, ChevronDown, ChevronRight,
  Package, Tag, Settings2, ToggleLeft, ToggleRight, X, Search, Ban,
  Sparkles, Loader2, Link, FileText, Check, AlertCircle
} from 'lucide-react';

const TAGS_OPTIONS = [
  { value: 'popolare', label: 'Popolare', color: '#EF4444' },
  { value: 'novita', label: 'Novita', color: '#8B5CF6' },
  { value: 'vegano', label: 'Vegano', color: '#10B981' },
  { value: 'vegetariano', label: 'Vegetariano', color: '#22C55E' },
  { value: 'piccante', label: 'Piccante', color: '#F97316' },
  { value: 'senza_glutine', label: 'Senza Glutine', color: '#3B82F6' },
];

/* ── Subcomponents ── */

function CategoryForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || { name: '', description: '', sort_order: 0, is_active: true });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Nome categoria *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
            data-testid="cat-name-input" placeholder="Es. Tacos, Burritos..." />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Ordine</label>
          <input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
            data-testid="cat-sort-input" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 mb-1 block">Descrizione</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none resize-none"
          data-testid="cat-desc-input" placeholder="Descrizione opzionale..." />
      </div>
      <div className="flex items-center gap-3 pt-1">
        <button onClick={() => onSave(form)} data-testid="cat-save-btn"
          className="px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A] transition-colors">
          Salva
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-slate-500 text-sm hover:text-slate-700 transition-colors">
          Annulla
        </button>
      </div>
    </div>
  );
}

function ItemForm({ initial, categories, modifierGroups, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    category_id: categories[0]?.id || '', name: '', description: '', price: 0,
    original_price: null, sort_order: 0, is_active: true, tags: [], allergens: [],
    modifier_group_ids: [], is_suggested: false,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTag = (tag) => {
    setForm(f => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag]
    }));
  };
  const toggleMod = (modId) => {
    setForm(f => ({
      ...f,
      modifier_group_ids: f.modifier_group_ids.includes(modId)
        ? f.modifier_group_ids.filter(m => m !== modId)
        : [...f.modifier_group_ids, modId]
    }));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Nome prodotto *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
            data-testid="item-name-input" placeholder="Es. Gordito Loco / 2 pezzi" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Categoria *</label>
          <select value={form.category_id} onChange={e => set('category_id', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
            data-testid="item-cat-select">
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 mb-1 block">Descrizione</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none resize-none"
          data-testid="item-desc-input" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Prezzo *</label>
          <input type="number" step="0.10" value={form.price} onChange={e => set('price', parseFloat(e.target.value) || 0)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
            data-testid="item-price-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Prezzo originale</label>
          <input type="number" step="0.10" value={form.original_price || ''} onChange={e => set('original_price', e.target.value ? parseFloat(e.target.value) : null)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
            data-testid="item-origprice-input" placeholder="Se in promo" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Ordine</label>
          <input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 mb-1 block">Tag</label>
        <div className="flex flex-wrap gap-2">
          {TAGS_OPTIONS.map(t => (
            <button key={t.value} onClick={() => toggleTag(t.value)} data-testid={`tag-${t.value}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                form.tags.includes(t.value) ? 'text-white' : 'bg-white'
              }`}
              style={{
                backgroundColor: form.tags.includes(t.value) ? t.color : undefined,
                borderColor: t.color,
                color: form.tags.includes(t.value) ? '#fff' : t.color,
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {modifierGroups.length > 0 && (
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Gruppi Modificatori</label>
          <div className="flex flex-wrap gap-2">
            {modifierGroups.map(m => (
              <button key={m.id} onClick={() => toggleMod(m.id)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                  form.modifier_group_ids.includes(m.id)
                    ? 'bg-[#D44E20] text-white border-[#D44E20]'
                    : 'bg-white text-slate-600 border-slate-300'
                }`}>
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <label className="flex items-center gap-2.5 pt-1 cursor-pointer select-none" data-testid="item-suggested-toggle">
        <input type="checkbox" checked={!!form.is_suggested} onChange={e => set('is_suggested', e.target.checked)} className="rounded w-4 h-4 accent-[#D44E20]" />
        <span className="text-sm text-slate-700">⭐ Suggerisci nel carrello <span className="text-xs text-slate-400">(cross-sell — proposto come "Aggiungi anche…")</span></span>
      </label>
      <div className="flex items-center gap-3 pt-1">
        <button onClick={() => onSave(form)} data-testid="item-save-btn"
          className="px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A] transition-colors">
          Salva
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-slate-500 text-sm hover:text-slate-700 transition-colors">
          Annulla
        </button>
      </div>
    </div>
  );
}

function ModifierForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || { name: '', required: false, min_selections: 0, max_selections: 1, options: [] });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addOption = () => setForm(f => ({ ...f, options: [...f.options, { name: '', price_delta: 0 }] }));
  const updateOption = (idx, key, val) => {
    setForm(f => {
      const opts = [...f.options];
      opts[idx] = { ...opts[idx], [key]: val };
      return { ...f, options: opts };
    });
  };
  const removeOption = (idx) => setForm(f => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Nome gruppo *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
            data-testid="mod-name-input" placeholder="Es. Scegli la bibita, Extra salse..." />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.required} onChange={e => set('required', e.target.checked)} className="rounded" />
            Obbligatorio
          </label>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Max selezioni</label>
            <input type="number" value={form.max_selections} onChange={e => set('max_selections', parseInt(e.target.value) || 1)}
              className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-sm text-center" min={1} />
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-slate-500">Opzioni</label>
          <button onClick={addOption} className="text-xs text-[#D44E20] font-semibold hover:underline flex items-center gap-1">
            <Plus size={12} /> Aggiungi opzione
          </button>
        </div>
        <div className="space-y-2">
          {form.options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input value={opt.name} onChange={e => updateOption(idx, 'name', e.target.value)}
                className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" placeholder="Nome opzione" />
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">+</span>
                <input type="number" step="0.10" value={opt.price_delta} onChange={e => updateOption(idx, 'price_delta', parseFloat(e.target.value) || 0)}
                  className="w-20 border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-right" />
                <span className="text-xs text-slate-400">EUR</span>
              </div>
              <button onClick={() => removeOption(idx)} className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                <X size={14} />
              </button>
            </div>
          ))}
          {form.options.length === 0 && <p className="text-xs text-slate-400 italic">Nessuna opzione. Clicca "Aggiungi opzione".</p>}
        </div>
      </div>
      <div className="flex items-center gap-3 pt-1">
        <button onClick={() => onSave(form)} data-testid="mod-save-btn"
          className="px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A] transition-colors">
          Salva
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-slate-500 text-sm hover:text-slate-700 transition-colors">
          Annulla
        </button>
      </div>
    </div>
  );
}

/* ── Main Component ── */

export default function AdminMenu() {
  const [tab, setTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingMod, setEditingMod] = useState(null);
  const [showNewCat, setShowNewCat] = useState(false);
  const [showNewItem, setShowNewItem] = useState(false);
  const [showNewMod, setShowNewMod] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [searchQ, setSearchQ] = useState('');
  const [aiImporting, setAiImporting] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiUrl, setAiUrl] = useState('');
  const [aiPdfFile, setAiPdfFile] = useState(null);
  const [aiSourceType, setAiSourceType] = useState('url');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, iRes, mRes, sRes] = await Promise.all([
        adminApi.get('/menu/categories'),
        adminApi.get('/menu/items'),
        adminApi.get('/menu/modifiers'),
        adminApi.get('/menu/stats'),
      ]);
      setCategories(cRes.data.categories || []);
      setItems(iRes.data.items || []);
      setModifierGroups(mRes.data.modifier_groups || []);
      setStats(sRes.data || {});
    } catch { toast.error('Errore caricamento menu'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCat = (catId) => setExpandedCats(e => ({ ...e, [catId]: !e[catId] }));

  // ── Category CRUD ──
  const saveCat = async (form) => {
    try {
      if (editingCat) {
        await adminApi.put(`/menu/categories/${editingCat.id}`, form);
        toast.success('Categoria aggiornata');
      } else {
        await adminApi.post('/menu/categories', form);
        toast.success('Categoria creata');
      }
      setEditingCat(null); setShowNewCat(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const deleteCat = async (catId) => {
    if (!window.confirm('Eliminare questa categoria?')) return;
    try {
      await adminApi.delete(`/menu/categories/${catId}`);
      toast.success('Categoria eliminata'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // ── Item CRUD ──
  const saveItem = async (form) => {
    try {
      if (editingItem) {
        await adminApi.put(`/menu/items/${editingItem.id}`, form);
        toast.success('Prodotto aggiornato');
      } else {
        await adminApi.post('/menu/items', form);
        toast.success('Prodotto creato');
      }
      setEditingItem(null); setShowNewItem(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const deleteItem = async (itemId) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await adminApi.delete(`/menu/items/${itemId}`);
      toast.success('Prodotto eliminato'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const toggleItem = async (itemId) => {
    try {
      const r = await adminApi.patch(`/menu/items/${itemId}/toggle`);
      toast.success(r.data.is_active ? 'Prodotto attivato' : 'Prodotto disattivato');
      load();
    } catch { toast.error('Errore'); }
  };

  const toggleSoldOut = async (item) => {
    const isSoldOut = item.sold_out_until && new Date(item.sold_out_until) > new Date();
    try {
      const r = await adminApi.patch(`/menu/items/${item.id}/sold-out`, { sold_out: !isSoldOut });
      if (r.data.sold_out) {
        const until = new Date(r.data.sold_out_until);
        toast.success(`"${item.name}" esaurito fino alle ${until.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`);
      } else {
        toast.success(`"${item.name}" di nuovo disponibile`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // ── Modifier CRUD ──
  const saveMod = async (form) => {
    try {
      if (editingMod) {
        await adminApi.put(`/menu/modifiers/${editingMod.id}`, form);
        toast.success('Modificatore aggiornato');
      } else {
        await adminApi.post('/menu/modifiers', form);
        toast.success('Modificatore creato');
      }
      setEditingMod(null); setShowNewMod(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const deleteMod = async (modId) => {
    if (!window.confirm('Eliminare questo gruppo modificatori?')) return;
    try {
      await adminApi.delete(`/menu/modifiers/${modId}`);
      toast.success('Modificatore eliminato'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const filteredItems = searchQ
    ? items.filter(i => i.name.toLowerCase().includes(searchQ.toLowerCase()) || i.description?.toLowerCase().includes(searchQ.toLowerCase()))
    : items;

  // ── AI Import ──
  const aiParse = async () => {
    setAiImporting(true);
    try {
      let r;
      if (aiSourceType === 'url') {
        r = await adminApi.post('/menu/ai-import/parse-url', { source_url: aiUrl });
      } else {
        const formData = new FormData();
        formData.append('file', aiPdfFile);
        r = await adminApi.post('/menu/ai-import/parse-pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
      }
      setAiPreview(r.data.parsed_menu);
      toast.success(`Trovati: ${r.data.categories_count} categorie, ${r.data.items_count} prodotti`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore AI import'); }
    setAiImporting(false);
  };

  const aiConfirm = async () => {
    if (!aiPreview) return;
    try {
      const r = await adminApi.post('/menu/ai-import/confirm', aiPreview);
      toast.success(r.data.message);
      setAiPreview(null); setAiUrl(''); setAiText(''); setTab('categories');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore importazione'); }
  };

  const TABS = [
    { key: 'categories', label: 'Categorie', icon: GripVertical, count: stats.total_categories },
    { key: 'items', label: 'Prodotti', icon: Package, count: stats.total_items },
    { key: 'modifiers', label: 'Modificatori', icon: Settings2, count: stats.total_modifier_groups },
    { key: 'ai-import', label: 'AI Import', icon: Sparkles, count: null },
  ];

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-menu-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1A1A1A]">Menu / Take-Away</h1>
        <p className="text-[#64748B] text-base mt-1">Gestisci il catalogo prodotti per ordini online e take-away</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Categorie', value: stats.total_categories || 0, active: stats.active_categories },
          { label: 'Prodotti', value: stats.total_items || 0, active: stats.active_items },
          { label: 'Modificatori', value: stats.total_modifier_groups || 0 },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-2xl font-bold text-[#1A1A1A]">{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}{s.active != null && ` (${s.active} attivi)`}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 mb-6 w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === t.key ? 'bg-white text-[#D44E20] shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <t.icon size={15} />
            {t.label}
            {t.count > 0 && <span className="text-[10px] bg-slate-200 px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ── Categories Tab ── */}
      {tab === 'categories' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-[#1A1A1A]">Categorie</h2>
            <button onClick={() => { setShowNewCat(true); setEditingCat(null); }} data-testid="add-category-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A] transition-colors">
              <Plus size={16} /> Nuova categoria
            </button>
          </div>
          {showNewCat && <CategoryForm onSave={saveCat} onCancel={() => setShowNewCat(false)} />}
          {categories.length === 0 && !showNewCat && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
              <GripVertical size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Nessuna categoria. Crea la prima o importa il menu con l'AI.</p>
            </div>
          )}
          {categories.map(cat => (
            <div key={cat.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {editingCat?.id === cat.id ? (
                <div className="p-4">
                  <CategoryForm initial={cat} onSave={saveCat} onCancel={() => setEditingCat(null)} />
                </div>
              ) : (
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => toggleCat(cat.id)}>
                    {expandedCats[cat.id] ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#1A1A1A]">{cat.name}</span>
                        {!cat.is_active && <span className="text-[10px] bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">Nascosta</span>}
                      </div>
                      <p className="text-xs text-slate-400">{cat.item_count || 0} prodotti{cat.description && ` · ${cat.description}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditingCat(cat)} className="p-2 text-slate-400 hover:text-[#D44E20] transition-colors rounded-lg hover:bg-slate-50">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteCat(cat.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-slate-50">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
              {expandedCats[cat.id] && !editingCat?.id !== cat.id && (
                <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/50">
                  {items.filter(i => i.category_id === cat.id).map(item => (
                    <div key={item.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${item.is_active ? 'text-[#1A1A1A]' : 'text-slate-400 line-through'}`}>{item.name}</span>
                          {item.is_suggested && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">⭐ Suggerito</span>}
                          {item.tags?.map(t => {
                            const tagDef = TAGS_OPTIONS.find(to => to.value === t);
                            return tagDef ? (
                              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: tagDef.color + '15', color: tagDef.color }}>{tagDef.label}</span>
                            ) : null;
                          })}
                        </div>
                        {item.description && <p className="text-xs text-slate-400 truncate max-w-md">{item.description}</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        {item.original_price && <span className="text-xs text-slate-400 line-through">{item.original_price.toFixed(2)} EUR</span>}
                        <span className="text-sm font-bold text-[#D44E20]">{item.price.toFixed(2)} EUR</span>
                        <button onClick={() => toggleItem(item.id)} className="p-1">
                          {item.is_active ? <ToggleRight size={18} className="text-green-500" /> : <ToggleLeft size={18} className="text-slate-300" />}
                        </button>
                      </div>
                    </div>
                  ))}
                  {items.filter(i => i.category_id === cat.id).length === 0 && (
                    <p className="text-xs text-slate-400 italic py-2">Nessun prodotto in questa categoria</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Items Tab ── */}
      {tab === 'items' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Cerca prodotto..."
                className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
                data-testid="item-search-input" />
            </div>
            <button onClick={() => { setShowNewItem(true); setEditingItem(null); }} data-testid="add-item-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A] transition-colors whitespace-nowrap">
              <Plus size={16} /> Nuovo prodotto
            </button>
          </div>
          {showNewItem && <ItemForm categories={categories} modifierGroups={modifierGroups} onSave={saveItem} onCancel={() => setShowNewItem(false)} />}
          {categories.length === 0 && !showNewItem && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
              <Package size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Crea prima almeno una categoria per aggiungere prodotti.</p>
            </div>
          )}
          {filteredItems.length === 0 && categories.length > 0 && !showNewItem && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
              <Package size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">{searchQ ? 'Nessun risultato' : 'Nessun prodotto. Aggiungine uno manualmente o importa con l\'AI.'}</p>
            </div>
          )}
          {filteredItems.map(item => {
            const cat = categories.find(c => c.id === item.category_id);
            return editingItem?.id === item.id ? (
              <ItemForm key={item.id} initial={item} categories={categories} modifierGroups={modifierGroups}
                onSave={saveItem} onCancel={() => setEditingItem(null)} />
            ) : (
              <div key={item.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold ${item.is_active ? 'text-[#1A1A1A]' : 'text-slate-400 line-through'}`}>{item.name}</span>
                    {cat && <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{cat.name}</span>}
                    {item.tags?.map(t => {
                      const tagDef = TAGS_OPTIONS.find(to => to.value === t);
                      return tagDef ? (
                        <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: tagDef.color + '15', color: tagDef.color }}>{tagDef.label}</span>
                      ) : null;
                    })}
                  </div>
                  {item.description && <p className="text-xs text-slate-400 truncate max-w-lg mt-0.5">{item.description}</p>}
                  {item.modifier_group_ids?.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {item.modifier_group_ids.map(mid => {
                        const mg = modifierGroups.find(m => m.id === mid);
                        return mg ? <span key={mid} className="text-[9px] bg-[#D44E20]/10 text-[#D44E20] px-1.5 py-0.5 rounded-full">{mg.name}</span> : null;
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  {item.sold_out_until && new Date(item.sold_out_until) > new Date() && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Esaurito</span>
                  )}
                  {item.original_price && <span className="text-xs text-slate-400 line-through">{item.original_price.toFixed(2)} EUR</span>}
                  <span className="text-sm font-bold text-[#D44E20]">{item.price.toFixed(2)} EUR</span>
                  <button onClick={() => toggleSoldOut(item)} className="p-2 rounded-lg hover:bg-amber-50 transition-colors" data-testid={`sold-out-${item.id}`}
                    title={item.sold_out_until && new Date(item.sold_out_until) > new Date() ? 'Rimetti disponibile' : 'Segna come esaurito oggi'}>
                    <Ban size={14} className={item.sold_out_until && new Date(item.sold_out_until) > new Date() ? 'text-amber-600' : 'text-slate-400 hover:text-amber-600'} />
                  </button>
                  <button onClick={() => toggleItem(item.id)} className="p-1" data-testid={`toggle-${item.id}`}>
                    {item.is_active ? <ToggleRight size={20} className="text-green-500" /> : <ToggleLeft size={20} className="text-slate-300" />}
                  </button>
                  <button onClick={() => setEditingItem(item)} className="p-2 text-slate-400 hover:text-[#D44E20] transition-colors rounded-lg hover:bg-slate-50">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deleteItem(item.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-slate-50">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modifiers Tab ── */}
      {tab === 'modifiers' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-[#1A1A1A]">Gruppi Modificatori</h2>
            <button onClick={() => { setShowNewMod(true); setEditingMod(null); }} data-testid="add-modifier-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A] transition-colors">
              <Plus size={16} /> Nuovo gruppo
            </button>
          </div>
          {showNewMod && <ModifierForm onSave={saveMod} onCancel={() => setShowNewMod(false)} />}
          {modifierGroups.length === 0 && !showNewMod && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
              <Settings2 size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Nessun modificatore. Es: "Scegli la bibita", "Extra salse", "Taglia".</p>
            </div>
          )}
          {modifierGroups.map(mod => (
            editingMod?.id === mod.id ? (
              <ModifierForm key={mod.id} initial={mod} onSave={saveMod} onCancel={() => setEditingMod(null)} />
            ) : (
              <div key={mod.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#1A1A1A]">{mod.name}</span>
                      {mod.required && <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">Obbligatorio</span>}
                      <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Max {mod.max_selections}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {mod.options?.map((opt, i) => (
                        <span key={i} className="text-xs bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg">
                          {opt.name} {opt.price_delta > 0 && <span className="text-[#D44E20] font-semibold">+{opt.price_delta.toFixed(2)} EUR</span>}
                          {opt.price_delta === 0 && <span className="text-slate-400">incluso</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditingMod(mod)} className="p-2 text-slate-400 hover:text-[#D44E20] transition-colors rounded-lg hover:bg-slate-50">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteMod(mod.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-slate-50">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* ── AI Import Tab ── */}
      {tab === 'ai-import' && (
        <div className="space-y-6">
          {!aiPreview ? (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D44E20] to-[#F59E0B] flex items-center justify-center">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A1A]">Importa Menu con AI</h2>
                  <p className="text-xs text-slate-500">Incolla un link Deliveroo/Glovo o carica un PDF del menu. L'AI lo struttura automaticamente.</p>
                </div>
              </div>

              <div className="flex gap-3 mb-4">
                <button onClick={() => setAiSourceType('url')} data-testid="ai-source-url"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                    aiSourceType === 'url' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-slate-500 border-slate-200'
                  }`}>
                  <Link size={14} /> Da URL
                </button>
                <button onClick={() => setAiSourceType('pdf')} data-testid="ai-source-pdf"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                    aiSourceType === 'pdf' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-slate-500 border-slate-200'
                  }`}>
                  <FileText size={14} /> Da PDF
                </button>
              </div>

              {aiSourceType === 'url' ? (
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">URL del menu (Deliveroo, Glovo, sito web...)</label>
                  <input value={aiUrl} onChange={e => setAiUrl(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none"
                    data-testid="ai-url-input"
                    placeholder="https://deliveroo.it/it/menu/..." />
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Carica il PDF del menu</label>
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-[#D44E20]/40 transition-colors">
                    <input type="file" accept=".pdf" id="pdf-upload"
                      onChange={e => setAiPdfFile(e.target.files?.[0] || null)}
                      className="hidden" data-testid="ai-pdf-input" />
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      <FileText size={32} className="mx-auto text-slate-300 mb-2" />
                      {aiPdfFile ? (
                        <p className="text-sm font-semibold text-[#D44E20]">{aiPdfFile.name}</p>
                      ) : (
                        <p className="text-sm text-slate-500">Clicca per selezionare un file PDF (max 20MB)</p>
                      )}
                    </label>
                  </div>
                </div>
              )}

              <button onClick={aiParse} disabled={aiImporting || (aiSourceType === 'url' ? !aiUrl : !aiPdfFile)}
                data-testid="ai-parse-btn"
                className="mt-4 flex items-center gap-2 px-6 py-2.5 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A] transition-colors disabled:opacity-40">
                {aiImporting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {aiImporting ? 'Analisi in corso...' : 'Analizza con AI'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <Check size={20} className="text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Menu analizzato con successo</p>
                  <p className="text-xs text-green-600">
                    {aiPreview.categories?.length || 0} categorie, {aiPreview.items?.length || 0} prodotti, {aiPreview.modifier_groups?.length || 0} modificatori
                  </p>
                </div>
              </div>

              {/* Preview categories */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-[#1A1A1A] mb-3">Categorie trovate</h3>
                <div className="flex flex-wrap gap-2">
                  {aiPreview.categories?.map((c, i) => (
                    <span key={i} className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-lg font-medium">{c.name}</span>
                  ))}
                </div>
              </div>

              {/* Preview items by category */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-[#1A1A1A] mb-3">Prodotti trovati ({aiPreview.items?.length})</h3>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {aiPreview.items?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#1A1A1A]">{item.name}</span>
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{item.category_name}</span>
                          {item.tags?.map(t => {
                            const td = TAGS_OPTIONS.find(to => to.value === t);
                            return td ? <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: td.color + '15', color: td.color }}>{td.label}</span> : null;
                          })}
                        </div>
                        {item.description && <p className="text-xs text-slate-400 truncate max-w-lg">{item.description}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                        {item.original_price && <span className="text-xs text-slate-400 line-through">{item.original_price.toFixed(2)} EUR</span>}
                        <span className="text-sm font-bold text-[#D44E20]">{item.price?.toFixed(2)} EUR</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {aiPreview.modifier_groups?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="font-bold text-[#1A1A1A] mb-3">Modificatori trovati</h3>
                  {aiPreview.modifier_groups.map((mg, i) => (
                    <div key={i} className="mb-2">
                      <span className="text-sm font-medium">{mg.name}</span>
                      <span className="text-xs text-slate-400 ml-2">({mg.options?.length} opzioni)</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={aiConfirm} data-testid="ai-confirm-btn"
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors">
                  <Check size={16} /> Importa nel catalogo
                </button>
                <button onClick={() => setAiPreview(null)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white text-slate-600 text-sm font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                  <X size={16} /> Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
